import { memo, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Environment, Lightformer, PerspectiveCamera, Grid, Edges } from "@react-three/drei";
import * as THREE from "three";
import { cn } from "@/lib/utils";
import { ModuleMesh, ACCENT } from "@/components/rig/ModuleMesh";
import { PhotoMapsProvider, assets } from "@/components/rig/rigAssets";
import { FOUNDATION_H, PALETTE, merge, box } from "@/components/rig/rigMaterials";
import { DEFAULT_FLOOR_HEIGHT, footprint, floorSpan, dims } from "@/components/rig/rigGeometry";
import { RigPad, Fence, Props, Joints, padRect, vanItems, workerItems, DRESSING_COLORS as C, FENCE_INSET, GATE_W } from "@/components/rig/SiteDressing";

/**
 * Forside-hero: ein boligrigg som blir bygd – slik han ser ut i riggdesign-modulen (same ModuleMesh, fototeksturar,
 * HDRI-lys, mjuke solskuggar, tomt med gjerde, folk, bil og rekvisittar). Koreografi (scenesekund):
 *   0,0  teiknerutenettet teiknar seg ut frå midten          0,35 riggplassen (grus) breier seg ut
 *   0,9  blå plan-«spøkelse» for kvar modul (som i editoren)  1,5  kranrekkjefølgje: modul for modul landar i planen
 *        (kvar landing gjev ein blå puls i bakken)             4,7  gjerdet reiser seg · skjøtebeslag kjem på
 *   5,2  servicebilen køyrer inn porten og parkerer            5,6  rekvisittar og folk kjem på plass
 *   Kameraet glir frå låg, fjern vinkel inn til endeleg innramming (0–7 s), og svingar så roleg att og fram.
 *   Innramminga er rekna eksakt frå hjørna til tomta mot lerretet sitt sideforhold – ingenting blir kutta.
 * Utan WebGL / med redusert rørsle / i miniatyrar vert berre plakaten vist.
 */

/* --- demo-rigg: typar som i backend/services/rig.py (farge, vindauge, dørside) --- */
const FH = DEFAULT_FLOOR_HEIGHT;
const TYPES = {
  B24: { id: "B24", code: "B24", name: "Boligmodul B24", kind: "bolig", width_m: 3.0, length_m: 8.4, height_m: 3.2, windows: 2, door_side: "south", color: "#ece7de" },
  G8: { id: "G8", code: "G8", name: "Gangmodul G8", kind: "gang", width_m: 3.0, length_m: 8.4, height_m: 3.0, windows: 2, door_side: "west", color: "#d9dee4" },
  TR1: { id: "TR1", code: "TR1", name: "Trappemodul TR1", kind: "trapp", width_m: 3.2, length_m: 3.2, height_m: 6.0, floors_span: 2, windows: 0, door_side: "east", color: "#75808b" },
  T1: { id: "T1", code: "T1", name: "Teknisk modul T1", kind: "teknisk", width_m: 2.5, length_m: 6.0, height_m: 3.0, windows: 0, door_side: "south", color: "#8b949d" },
};

/** Boligrigg i to etasjar: to rekkjer à seks B24 rundt ein midtgang, trappetårn i aust, teknisk modul på utsida. */
function buildLayout() {
  const out = [];
  let n = 0;
  const add = (type, x, z, rot, floor) => out.push({ id: `h${n++}`, module_type_id: type, x, z, rot, floor });
  for (const floor of [0, 1]) {
    for (let i = 0; i < 6; i++) add("B24", i * 3, 0, 0, floor); // nordrekkje, dør mot gangen (sør)
    add("G8", 0, 8.4, 90, floor); // midtgang 0–16,8 m
    add("G8", 8.4, 8.4, 90, floor);
    for (let i = 0; i < 6; i++) add("B24", i * 3, 11.4, 180, floor); // sørrekkje, dør mot gangen (nord)
    if (floor === 0) {
      add("TR1", 16.8, 8.3, 0, 0); // trappetårn ved gangens austende, spenner begge etasjar
      add("T1", -3.7, 1.2, 0, 0); // teknisk modul på utsida i vest
    }
  }
  return out;
}
const LAYOUT = buildLayout();
export const HERO_MODULE_COUNT = LAYOUT.length;

/* bounding box of the finished rig + the pad around it – camera target, fit and shadow frustum */
const BOUNDS = (() => {
  let x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity, top = 0;
  for (const m of LAYOUT) {
    const t = TYPES[m.module_type_id];
    const fp = footprint(t, m.rot);
    x0 = Math.min(x0, m.x); x1 = Math.max(x1, m.x + fp.sx);
    z0 = Math.min(z0, m.z); z1 = Math.max(z1, m.z + fp.sz);
    top = Math.max(top, (m.floor + floorSpan(t)) * FH + FOUNDATION_H);
  }
  return { x0, x1, z0, z1, top, cx: (x0 + x1) / 2, cz: (z0 + z1) / 2, w: x1 - x0, d: z1 - z0 };
})();
const PAD = padRect(BOUNDS);
const PADC = { cx: (PAD.x0 + PAD.x1) / 2, cz: (PAD.z0 + PAD.z1) / 2, w: PAD.x1 - PAD.x0, d: PAD.z1 - PAD.z0 };
const GATE_Z = PAD.z1 - FENCE_INSET; // fence line on the south side (gate in the middle)
/* everything the camera must keep inside the canvas: the pad, the open gate leaves (swing ~1,6 m outside) and the roof */
const FIT_BOX = { x0: PAD.x0 - 0.6, x1: PAD.x1 + 0.6, z0: PAD.z0 - 0.6, z1: PAD.z1 + 2.4, y0: 0, y1: BOUNDS.top + 0.5 };
const FIT_CORNERS = [];
for (const x of [FIT_BOX.x0, FIT_BOX.x1]) for (const y of [FIT_BOX.y0, FIT_BOX.y1]) for (const z of [FIT_BOX.z0, FIT_BOX.z1]) FIT_CORNERS.push([x, y, z]);

/* --- tidslinje (scenesekund) --- */
const T_PAD = 0.35, T_GHOST = 0.9, GHOST_STEP = 0.035, T_BUILD = 1.5, STEP = 0.1, DUR = 0.64, DROP = 9;
const T_LAST = T_BUILD + (LAYOUT.length - 1) * STEP + DUR; // last module down
const T_FENCE = T_LAST - 0.3;
const T_VAN = T_FENCE + 0.5, VAN_DUR = 3.3;
const T_PROPS = T_FENCE + 0.7;
const T_CAM = 7.0;
export const HERO_BUILD_SECONDS = T_VAN + VAN_DUR + 0.4;
const easeOut = (p) => 1 - Math.pow(1 - p, 3);
const easeOutQuart = (p) => 1 - Math.pow(1 - p, 4);
const easeInOut = (p) => (p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2);
const easeBack = (p) => { const c1 = 1.2, c3 = c1 + 1; return 1 + c3 * Math.pow(p - 1, 3) + c1 * Math.pow(p - 1, 2); };
const seg = (t, from, dur) => Math.max(0, Math.min(1, (t - from) / dur));

function webglAvailable() {
  try {
    const c = document.createElement("canvas");
    return !!(window.WebGL2RenderingContext && (c.getContext("webgl2") || c.getContext("webgl")));
  } catch {
    return false;
  }
}

const SKY = assets.hdri.find((h) => h.id === "kloofendal_43d_clear_puresky") || assets.hdri[0];

/* Studio fallback when the HDRI is unavailable – soft top light, two cool fills */
function StudioEnvironment() {
  return (
    <Environment resolution={128} frames={1} background={false}>
      <Lightformer intensity={1.6} rotation-x={Math.PI / 2} position={[0, 6, 0]} scale={[14, 14, 1]} />
      <Lightformer intensity={0.8} rotation-y={Math.PI / 2} position={[-8, 2, 0]} scale={[8, 4, 1]} color="#dbe6f7" />
      <Lightformer intensity={0.6} rotation-y={-Math.PI / 2} position={[8, 2, 0]} scale={[8, 4, 1]} color="#f3efe6" />
    </Environment>
  );
}

function Probe({ onReady }) {
  useEffect(() => { onReady?.(); }, [onReady]);
  return null;
}

/* Sun from the south-west, 48° up – crisp but soft-edged shadows (PCSS). Frustum covers pad + the van's approach. */
function Sun() {
  const ref = useRef(null);
  const target = useMemo(() => new THREE.Object3D(), []);
  const el = THREE.MathUtils.degToRad(48), az = THREE.MathUtils.degToRad(215); // azimuth from north, clockwise
  const dist = 80;
  const pos = [PADC.cx + Math.sin(az) * Math.cos(el) * dist, Math.sin(el) * dist, PADC.cz - Math.cos(az) * Math.cos(el) * dist];
  useEffect(() => {
    const l = ref.current;
    if (!l) return;
    target.position.set(PADC.cx, 0, PADC.cz + 4);
    l.target = target;
    const r = Math.max(PADC.w, PADC.d) * 0.8 + 6;
    Object.assign(l.shadow.camera, { left: -r, right: r, top: r, bottom: -r, near: 10, far: dist + 60 });
    l.shadow.camera.updateProjectionMatrix();
  }, [target]);
  return (
    <>
      <primitive object={target} />
      <directionalLight ref={ref} position={pos} intensity={3.2} color="#fff4e6" castShadow shadow-mapSize={[2048, 2048]} shadow-bias={-0.0002} shadow-normalBias={0.03} />
    </>
  );
}

/* Camera. Each frame the final framing is solved exactly: place the camera on the (azimuth, elevation) ray, project the
   eight corners of FIT_BOX, then scale the distance and shift the look-at point until the box sits centred inside the
   canvas with the given margins (4 iterations – a few dozen projections). The cinematic approach only multiplies that
   distance and lowers/turns the ray, so the site is never cut off at any aspect ratio. After the approach the camera
   swings slowly back and forth around the hero angle (gate, people and stair tower stay in view) and follows the mouse
   a little (parallax in the camera – no CSS 3D transform around the WebGL canvas). */
const MARGIN_X = 0.03, MARGIN_Y = 0.04, BIAS_Y = 0.015; // BIAS_Y > 0 lifts the composition slightly (more air below)
const _p = new THREE.Vector3(), _right = new THREE.Vector3(), _up = new THREE.Vector3();
function solveFraming(camera, aspect, az, el, look) {
  const dx = Math.sin(az) * Math.cos(el), dy = Math.sin(el), dz = Math.cos(az) * Math.cos(el);
  const tanF = Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2);
  let d = 70;
  for (let it = 0; it < 4; it++) {
    camera.position.set(look.x + dx * d, look.y + dy * d, look.z + dz * d);
    camera.lookAt(look);
    camera.updateMatrixWorld(true);
    let xmin = Infinity, xmax = -Infinity, ymin = Infinity, ymax = -Infinity;
    for (const c of FIT_CORNERS) {
      _p.set(c[0], c[1], c[2]).project(camera);
      if (_p.x < xmin) xmin = _p.x; if (_p.x > xmax) xmax = _p.x;
      if (_p.y < ymin) ymin = _p.y; if (_p.y > ymax) ymax = _p.y;
    }
    const need = Math.max((xmax - xmin) / 2 / (1 - MARGIN_X), (ymax - ymin) / 2 / (1 - MARGIN_Y));
    const cx = (xmin + xmax) / 2, cy = (ymin + ymax) / 2 - BIAS_Y;
    _right.setFromMatrixColumn(camera.matrixWorld, 0);
    _up.setFromMatrixColumn(camera.matrixWorld, 1);
    look.addScaledVector(_right, cx * tanF * aspect * d).addScaledVector(_up, cy * tanF * d);
    d *= need;
  }
  return d;
}
function HeroCamera({ clock, pointer, elevationDeg = 24, azimuth0 = 0.78 }) {
  const { camera, size } = useThree();
  const look = useMemo(() => new THREE.Vector3(), []);
  const smooth = useRef({ x: 0, y: 0 });
  useFrame((_, dt) => {
    const t = clock.current.t;
    const a = easeInOut(seg(t, 0, T_CAM));
    // mouse parallax, damped
    const k = 1 - Math.exp(-dt * 3);
    smooth.current.x += (pointer.current.x - smooth.current.x) * k;
    smooth.current.y += (pointer.current.y - smooth.current.y) * k;
    const sway = a >= 1 ? Math.sin((t - T_CAM) * (Math.PI * 2) / 46) * 0.14 : 0;
    const az = azimuth0 - 0.5 * (1 - a) + sway + smooth.current.x * 0.06;
    const el = THREE.MathUtils.degToRad(elevationDeg - 10 * (1 - a) - smooth.current.y * 1.6);
    look.set(PADC.cx, BOUNDS.top * 0.35, PADC.cz);
    const aspect = size.width / Math.max(1, size.height);
    const d = solveFraming(camera, aspect, az, el, look) * (1 + 0.32 * (1 - a));
    camera.position.set(look.x + Math.sin(az) * Math.cos(el) * d, look.y + Math.sin(el) * d, look.z + Math.cos(az) * Math.cos(el) * d);
    camera.lookAt(look);
  });
  return null;
}

/* Drafting grid on the paper – 1 m cells, 6 m sections (two module widths) – the same metric grid the rig designer
   uses. It draws itself out from the centre (fade radius 0 → 90 m over 1.4 s) and keeps fading softly with distance.
   It sits just below the pad (−5 mm) so the gravel covers it cleanly – no coplanar shimmer. */
const GRID_R = 90;
function DraftingGrid({ clock }) {
  const ref = useRef(null);
  useFrame(() => {
    const u = ref.current?.material?.uniforms;
    if (!u?.fadeDistance) return;
    u.fadeDistance.value = Math.max(0.01, GRID_R * easeOut(seg(clock.current.t, 0, 1.4)));
  });
  return (
    <Grid ref={ref} position={[PADC.cx, -0.005, PADC.cz]} args={[10, 10]} cellSize={1} cellThickness={0.55} cellColor="#cfd4db" sectionSize={6} sectionThickness={1} sectionColor="#aeb6c2" fadeDistance={0.01} fadeStrength={1.7} fadeFrom={0} infiniteGrid followCamera={false} />
  );
}

/* Plan «ghosts»: the placement outline the rig designer shows before a module is dropped – translucent accent box with
   dashed edges. Each one grows up from the ground in build order and disappears the moment its module has landed. */
function Ghosts({ clock }) {
  const refs = useRef([]);
  const boxes = useMemo(() => LAYOUT.map((m) => {
    const t = TYPES[m.module_type_id];
    const fp = footprint(t, m.rot);
    const h = Math.min(dims(t).h, FH * floorSpan(t)) - 0.06;
    return { key: m.id, size: [fp.sx - 0.04, h, fp.sz - 0.04], pos: [m.x + fp.sx / 2, m.floor * FH + FOUNDATION_H, m.z + fp.sz / 2] };
  }), []);
  useFrame(() => {
    const t = clock.current.t;
    for (let i = 0; i < boxes.length; i++) {
      const g = refs.current[i];
      if (!g) continue;
      const landedAt = T_BUILD + i * STEP + DUR;
      const p = seg(t, T_GHOST + i * GHOST_STEP, 0.35);
      const on = p > 0 && t < landedAt;
      g.visible = on;
      if (on) g.scale.y = Math.max(0.001, easeOut(p));
    }
  });
  return (
    <group>
      {boxes.map((b, i) => (
        <group key={b.key} ref={(el) => { refs.current[i] = el; }} position={b.pos} visible={false}>
          <mesh position={[0, b.size[1] / 2, 0]} raycast={() => null}>
            <boxGeometry args={b.size} />
            <meshBasicMaterial color={ACCENT} transparent opacity={0.09} depthWrite={false} />
            <Edges color={ACCENT} threshold={15} lineWidth={1.1} transparent opacity={0.85} dashed dashSize={0.4} gapSize={0.25} />
          </mesh>
        </group>
      ))}
    </group>
  );
}

/* Landing pulse: a thin accent frame around the footprint that expands and fades the moment a module touches down –
   the «snapped into place» feedback from the editor. Ground floor: on the pad; upper floor: at the seam. */
function rectRing(w, l, band) {
  const s = new THREE.Shape();
  s.moveTo(-w / 2 - band, -l / 2 - band); s.lineTo(w / 2 + band, -l / 2 - band); s.lineTo(w / 2 + band, l / 2 + band); s.lineTo(-w / 2 - band, l / 2 + band); s.closePath();
  const hole = new THREE.Path();
  hole.moveTo(-w / 2, -l / 2); hole.lineTo(-w / 2, l / 2); hole.lineTo(w / 2, l / 2); hole.lineTo(w / 2, -l / 2); hole.closePath();
  s.holes.push(hole);
  const g = new THREE.ShapeGeometry(s);
  g.rotateX(-Math.PI / 2);
  return g;
}
const PULSE_DUR = 0.55;
function LandingPulses({ clock }) {
  const refs = useRef([]);
  const items = useMemo(() => LAYOUT.map((m, i) => {
    const t = TYPES[m.module_type_id];
    const fp = footprint(t, m.rot);
    return { key: m.id, geo: rectRing(fp.sx, fp.sz, 0.32), pos: [m.x + fp.sx / 2, m.floor * FH + (m.floor === 0 ? 0.012 : FOUNDATION_H + 0.02), m.z + fp.sz / 2], at: T_BUILD + i * STEP + DUR };
  }), []);
  useEffect(() => () => items.forEach((it) => it.geo.dispose()), [items]);
  useFrame(() => {
    const t = clock.current.t;
    for (let i = 0; i < items.length; i++) {
      const g = refs.current[i];
      if (!g) continue;
      const p = (t - items[i].at) / PULSE_DUR;
      const on = p > 0 && p < 1;
      g.visible = on;
      if (!on) continue;
      const e = easeOut(p);
      const s = 1 + 0.14 * e;
      g.scale.set(s, 1, s);
      g.material.opacity = 0.95 * (1 - e);
    }
  });
  return (
    <group>
      {items.map((it, i) => (
        <mesh key={it.key} ref={(el) => { refs.current[i] = el; }} geometry={it.geo} position={it.pos} visible={false} raycast={() => null}>
          <meshBasicMaterial color={ACCENT} transparent opacity={0} depthWrite={false} toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}

/* The rig itself: every module animated by its own crane slot; a module casts its shadow only once it has landed (no
   «flying» shadow blobs on the paper) – progress is reported as whole modules landed */
function setCast(group, on) {
  group.traverse((o) => { if (o.isMesh && o.userData.cast !== undefined) o.castShadow = on && o.userData.cast; });
}
function Rig({ clock, onProgress, onAssembled }) {
  const refs = useRef([]);
  const state = useRef([]); // per module: "hidden" | "flying" | "landed"
  const landed = useRef(-1);
  const done = useRef(false);
  useFrame(() => {
    const t = clock.current.t - T_BUILD;
    let count = 0;
    for (let i = 0; i < LAYOUT.length; i++) {
      const g = refs.current[i];
      if (!g) continue;
      if (state.current[i] === undefined) {
        // remember which meshes are shadow casters, then switch casting off until the module has landed
        g.traverse((o) => { if (o.isMesh) o.userData.cast = o.castShadow; });
        setCast(g, false);
        state.current[i] = "hidden";
      }
      const p = (t - i * STEP) / DUR;
      if (p <= 0) { g.visible = false; continue; }
      g.visible = true;
      const e = easeOutQuart(Math.min(1, p));
      g.position.y = (1 - e) * DROP;
      g.rotation.y = (1 - e) * (i % 2 ? 0.06 : -0.06);
      if (p >= 1) {
        count++;
        if (state.current[i] !== "landed") { state.current[i] = "landed"; setCast(g, true); }
      } else state.current[i] = "flying";
    }
    if (count !== landed.current) { landed.current = count; onProgress?.(count); }
    if (!done.current && count === LAYOUT.length) { done.current = true; onAssembled?.(); }
  });
  return (
    <group>
      {LAYOUT.map((m, i) => {
        const type = TYPES[m.module_type_id];
        return <ModuleMesh key={m.id} ref={(el) => { refs.current[i] = el; }} m={m} type={type} floorHeight={FH} color={type.color} />;
      })}
    </group>
  );
}

/* Service van (the designer's scale reference) driving in through the open gate and parking in the marked bay. The
   path is a cubic Bézier from outside the fence, through the gate, sweeping west into the bay; heading = tangent. */
const BAY_D = 2.6, BAY_L = 6.2;
const BAY = (() => {
  const cx = (PAD.x0 + PAD.x1) / 2;
  const zc = GATE_Z - 0.35 - BAY_D / 2;
  const xe = cx - 4, xw = xe - BAY_L;
  return { cx, zc, xe, xw, vanX: (xe + xw) / 2 };
})();
const VAN_PATH = (() => {
  const p0 = new THREE.Vector3(BAY.cx + 0.3, 0, GATE_Z + 17);
  const p1 = new THREE.Vector3(BAY.cx + 0.3, 0, GATE_Z - 0.6);
  const p2 = new THREE.Vector3(BAY.vanX + 5.2, 0, BAY.zc);
  const p3 = new THREE.Vector3(BAY.vanX, 0, BAY.zc);
  return new THREE.CubicBezierCurve3(p0, p1, p2, p3);
})();
const _vp = new THREE.Vector3(), _vt = new THREE.Vector3();
function Van({ clock }) {
  const ref = useRef(null);
  const parts = useMemo(() => {
    const v = vanItems();
    return Object.fromEntries(Object.entries(v).map(([k, items]) => [k, merge(items)]));
  }, []);
  useFrame(() => {
    const g = ref.current;
    if (!g) return;
    const t = clock.current.t;
    g.visible = t > T_VAN - 0.05;
    if (!g.visible) return;
    const p = easeInOut(seg(t, T_VAN, VAN_DUR));
    VAN_PATH.getPointAt(p, _vp);
    VAN_PATH.getTangentAt(Math.min(0.999, p), _vt);
    g.position.set(_vp.x, 0, _vp.z);
    g.rotation.y = Math.atan2(_vt.x, _vt.z);
  });
  return (
    <group ref={ref} visible={false} raycast={() => null}>
      <mesh geometry={parts.paint} castShadow><meshStandardMaterial color={C.paint} roughness={0.32} metalness={0.25} /></mesh>
      <mesh geometry={parts.glass}><meshStandardMaterial color={PALETTE.glass} roughness={0.08} metalness={0.6} envMapIntensity={1.4} /></mesh>
      <mesh geometry={parts.rubber} castShadow><meshStandardMaterial color={C.rubber} roughness={0.92} /></mesh>
      <mesh geometry={parts.hubs}><meshStandardMaterial color={PALETTE.galv} roughness={0.35} metalness={0.7} /></mesh>
      <mesh geometry={parts.brand}><meshStandardMaterial color={ACCENT} roughness={0.4} metalness={0.2} /></mesh>
    </group>
  );
}

/* Workers in hi-vis (procedural, from the designer) – each pops up from his feet with a little overshoot */
function Worker({ x, z, yaw, at, clock }) {
  const ref = useRef(null);
  const parts = useMemo(() => {
    const w = workerItems();
    return Object.fromEntries(Object.entries(w).map(([k, items]) => [k, merge(items)]));
  }, []);
  useFrame(() => {
    const g = ref.current;
    if (!g) return;
    const s = easeBack(seg(clock.current.t, at, 0.6));
    g.visible = s > 0;
    if (g.visible) g.scale.setScalar(Math.max(0.001, s));
  });
  return (
    <group ref={ref} position={[x, 0, z]} rotation={[0, yaw, 0]} visible={false} raycast={() => null}>
      <mesh geometry={parts.dark} castShadow><meshStandardMaterial color={C.dark} roughness={0.85} /></mesh>
      <mesh geometry={parts.hivis} castShadow><meshStandardMaterial color={C.hivis} roughness={0.7} /></mesh>
      <mesh geometry={parts.stripe}><meshStandardMaterial color={C.stripe} roughness={0.3} metalness={0.2} /></mesh>
      <mesh geometry={parts.skin}><meshStandardMaterial color={C.skin} roughness={0.7} /></mesh>
      <mesh geometry={parts.helmet} castShadow><meshStandardMaterial color={C.helmet} roughness={0.3} metalness={0.1} /></mesh>
    </group>
  );
}
const WORKERS = (() => {
  const b = BOUNDS;
  const w1 = { x: BAY.cx - 3.3, z: GATE_Z - 3.6, yaw: -Math.PI / 2 + 0.35, at: T_VAN + VAN_DUR - 0.25 }; // meets the van
  const w2 = { x: b.x0 - 1.7, z: b.z1 + 1.6, at: T_FENCE + 0.9 }; // south-west corner, looking at the building
  w2.yaw = Math.atan2(b.x0 + 3 - w2.x, b.z0 - w2.z);
  const w3 = { x: b.x1 + 2.2, z: b.z1 - 2.5, at: T_FENCE + 1.25 }; // by the stair tower
  w3.yaw = Math.atan2(b.x1 - 3 - w3.x, b.z1 - 6 - w3.z);
  const w4 = { x: BAY.cx + GATE_W / 2 + 0.9, z: GATE_Z - 2.3, yaw: Math.PI + 0.5, at: T_FENCE + 1.55 }; // at the gate
  return [w1, w2, w3, w4];
})();

/* Site dressing from the designer, staged: the gravel pad spreads out from its centre first, the fence rises once the
   last module is down, the bay markings and joint flashings appear, then props come up through the pad. */
function Site({ clock }) {
  const pad = useRef(null), fence = useRef(null), props = useRef(null), joints = useRef(null), bay = useRef(null);
  const typesById = useMemo(() => TYPES, []);
  const bayLines = useMemo(() => merge([box(0.12, 0.012, BAY_D, BAY.xe, 0.006, BAY.zc), box(0.12, 0.012, BAY_D, BAY.xw, 0.006, BAY.zc)]), []);
  useFrame(() => {
    const t = clock.current.t;
    if (pad.current) { const s = easeOut(seg(t, T_PAD, 0.9)); pad.current.visible = s > 0; pad.current.scale.set(Math.max(0.001, s), 1, Math.max(0.001, s)); }
    if (fence.current) { const s = easeOut(seg(t, T_FENCE, 0.8)); fence.current.visible = s > 0; fence.current.scale.y = Math.max(0.001, s); }
    if (props.current) { const s = easeOut(seg(t, T_PROPS, 0.9)); props.current.visible = s > 0; props.current.position.y = -2.6 * (1 - s); }
    if (joints.current) joints.current.visible = t > T_FENCE;
    if (bay.current) bay.current.visible = t > T_FENCE + 0.2;
  });
  return (
    <group>
      <group ref={pad} position={[PADC.cx, 0, PADC.cz]} visible={false}>
        <group position={[-PADC.cx, 0, -PADC.cz]}><RigPad P={PAD} kind="grus" faded={false} /></group>
      </group>
      <group ref={fence} visible={false}><Fence P={PAD} /></group>
      <mesh ref={bay} geometry={bayLines} visible={false} raycast={() => null}><meshStandardMaterial color={C.line} roughness={0.8} /></mesh>
      <group ref={props} visible={false}><Props P={PAD} b={BOUNDS} /></group>
      <group ref={joints} visible={false}><Joints modules={LAYOUT} types={typesById} floorHeight={FH} /></group>
      <Van clock={clock} />
      {WORKERS.map((w, i) => <Worker key={i} {...w} clock={clock} />)}
    </group>
  );
}

/* Wall-clock scene time (frame-rate independent); a long pause (tab switch) advances at most 0.25 s per frame */
function SceneClock({ clock, speed = 1 }) {
  const last = useRef(null);
  useFrame(() => {
    const now = performance.now();
    if (last.current != null) clock.current.t += Math.min(0.25, (now - last.current) / 1000) * speed;
    last.current = now;
  });
  return null;
}

/* The canvas is mounted only when the slide is live – a scene clock starting at 0 drives build, site and camera.
   Memoised: progress updates in the slide must not re-render the scene graph. */
const HeroScene = memo(function HeroScene({ onReady, onProgress, onAssembled, pointer, active = true, speed = 1, startAt = 0 }) {
  const clock = useRef({ t: startAt });
  const readyOnce = useRef(false);
  const ready = useCallback(() => { if (!readyOnce.current) { readyOnce.current = true; onReady?.(); } }, [onReady]);
  // if the HDRI takes long (slow network) we still go live after a moment – lighting improves when it arrives
  useEffect(() => { const t = setTimeout(ready, 2600); return () => clearTimeout(t); }, [ready]);
  return (
    /* shadows="soft" = PCFSoftShadowMap: temporally stable. drei's <SoftShadows> (PCSS) seeds its noise from
       gl_FragCoord, so with a camera that moves every frame the penumbra pattern re-rolls each frame and the shadow on
       the facades «boils» – fine in the editor (render on demand, static camera), not in a cinematic loop. */
    <Canvas shadows="soft" dpr={[1, 1.5]} frameloop={active ? "always" : "never"} gl={{ alpha: true, antialias: true, powerPreference: "high-performance", toneMapping: THREE.ACESFilmicToneMapping, toneMappingExposure: 1.0 }} onCreated={({ gl }) => gl.setClearColor(0x000000, 0)} style={{ background: "transparent" }}>
      <PhotoMapsProvider>
        <SceneClock clock={clock} speed={speed} />
        <PerspectiveCamera makeDefault fov={28} near={1} far={600} position={[60, 30, 60]} />
        <HeroCamera clock={clock} pointer={pointer} />
        <hemisphereLight args={["#ffffff", "#8e969f", 0.16]} />
        <Sun />
        <Suspense fallback={<StudioEnvironment />}>
          <Environment files={SKY.file} background={false} environmentIntensity={0.6} environmentRotation={[0, Math.PI * 0.35, 0]} />
          <Probe onReady={ready} />
        </Suspense>
        {/* shadow-catcher: the ground is the paper – only the shadow is drawn. 1 cm under the pad (no coplanar fight). */}
        <mesh rotation-x={-Math.PI / 2} position={[PADC.cx, -0.01, PADC.cz]} receiveShadow>
          <planeGeometry args={[260, 260]} />
          <shadowMaterial transparent opacity={0.3} color="#0B1220" />
        </mesh>
        <DraftingGrid clock={clock} />
        <Site clock={clock} />
        <Ghosts clock={clock} />
        <LandingPulses clock={clock} />
        <Rig clock={clock} onProgress={onProgress} onAssembled={onAssembled} />
      </PhotoMapsProvider>
    </Canvas>
  );
});

/**
 * Hero-komponenten på forsida. Tittelen kjem først; så tonar 3D inn og riggen vert bygd medan kameraet glir på plass.
 * Musa gjev ein svak parallakse i kameraet. `onProgress(count)` rapporterer modular som har landa.
 */
/* `active` (standard true): false set renderløkka på pause (frameloop «never») – brukt når riggen er utanfor biletet i tilbodet.
   Sceneklokka er delta-basert (maks 0,25 s per bilete), så byggjinga held fram der ho slapp når ho blir aktiv att.
   `speed` (standard 1) skalerer tempoet i heile sekvensen; `startAt` (sekund) hoppar over starten av tidslinja. */
export function RigHero({ className, testid = "pres-cover-visual", onProgress, children, active = true, speed = 1, startAt = 0 }) {
  const [gl] = useState(() => typeof window !== "undefined" && webglAvailable());
  const [live, setLive] = useState(false);
  const [assembled, setAssembled] = useState(false);
  const [poster, setPoster] = useState(false);
  const [reduced, setReduced] = useState(false);
  const [thumb, setThumb] = useState(false);
  const ref = useRef(null);
  const pointer = useRef({ x: 0, y: 0 });
  useEffect(() => { setThumb(!!ref.current?.closest(".pres-thumb-scale")); }, []);
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!mq) return undefined;
    const fn = () => setReduced(mq.matches);
    fn();
    mq.addEventListener?.("change", fn);
    return () => mq.removeEventListener?.("change", fn);
  }, []);
  const use3d = gl && !reduced && !thumb;
  // poster as fallback: at once when 3D is not an option, otherwise only after 3.5 s without a live scene
  useEffect(() => {
    if (!use3d) { setPoster(true); return undefined; }
    if (live) return undefined;
    const t = setTimeout(() => setPoster(true), 3500);
    return () => clearTimeout(t);
  }, [use3d, live]);
  const onReady = useCallback(() => setLive(true), []);
  const onAssembled = useCallback(() => setAssembled(true), []);
  // mouse position (−0.5 … 0.5) read by the camera each frame – no React state, no CSS transforms
  useEffect(() => {
    if (!use3d) return undefined;
    const onMove = (e) => { pointer.current.x = e.clientX / window.innerWidth - 0.5; pointer.current.y = e.clientY / window.innerHeight - 0.5; };
    const onLeave = () => { pointer.current.x = 0; pointer.current.y = 0; };
    window.addEventListener("pointermove", onMove, { passive: true });
    document.addEventListener("pointerleave", onLeave);
    return () => { window.removeEventListener("pointermove", onMove); document.removeEventListener("pointerleave", onLeave); };
  }, [use3d]);
  return (
    <div ref={ref} className={cn("pres-cover-visual", className)} aria-hidden="true" data-testid={testid} data-live={live ? "true" : "false"} data-assembled={assembled ? "true" : "false"}>
      <div className="pres-cover-stage">
        {poster && <img src="/presentasjon/rig-poster.png" alt="" className={cn("pres-cover-img", live && "pres-cover-img-hidden")} draggable={false} onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />}
        {use3d && (
          <div className={cn("pres-cover-3d", live && "pres-cover-3d-live")}>
            <HeroScene onReady={onReady} onProgress={onProgress} onAssembled={onAssembled} pointer={pointer} active={active} speed={speed} startAt={startAt} />
          </div>
        )}
      </div>
      {children}
    </div>
  );
}
